import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getAlert } from "../lib/api";
import { ArrowLeft, MapPin, Clock, Copy, Radio } from "lucide-react";
import { toast } from "sonner";
import { useT } from "../lib/lang";
import { InterventionCard } from "../components/InterventionStatus";
import { useAlertStatusStream } from "../lib/useAlertStatusStream";

export default function SharePage() {
  const { id } = useParams();
  const [t, labels] = useT();
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async (initial) => {
      try {
        const a = await getAlert(id);
        if (!cancelled) setAlert(a);
      } catch (e) {
        if (!cancelled && initial) setError(e?.response?.data?.detail || t.alertNotFound);
      } finally {
        if (!cancelled && initial) setLoading(false);
      }
    };
    load(true);
    // Slow safety-net poll (60 s) — primary source of truth is the WS below.
    // Polls keep the UI fresh if the socket is closed/blocked by a proxy.
    const intv = setInterval(() => load(false), 60000);
    return () => { cancelled = true; clearInterval(intv); };
  }, [id]);

  // Live intervention updates via WebSocket — merges pushes straight into
  // the alert state so <InterventionCard /> re-renders instantly.
  const { live } = useAlertStatusStream(id, {
    onIntervention: (intervention) => {
      setAlert((prev) => (prev ? { ...prev, intervention } : prev));
    },
  });

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success(t.linkCopied);
    } catch (_e) { toast.error(t.copyFailed); }
  };

  if (loading) {
    return <div className="p-6 text-sm text-rescue-mute" data-testid="share-loading">{t.loading}</div>;
  }
  if (error || !alert) {
    return (
      <div className="p-6" data-testid="share-error">
        <Link to="/" className="text-sm text-rescue-red font-bold uppercase tracking-[0.2em]">← {t.back}</Link>
        <div className="mt-6 text-rescue-ink font-display text-2xl font-black">{t.alertNotFound}</div>
        <div className="text-sm text-rescue-mute mt-2">{error}</div>
      </div>
    );
  }

  const c = alert.coordinates;
  const label = labels[alert.emergency_type]?.label || alert.emergency_type;

  return (
    <div className="flex-1 flex flex-col" data-testid="share-page">
      <div className="bg-rescue-ink text-white px-5 py-6">
        <div className="flex items-start justify-between gap-3">
          <Link to="/" className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] uppercase opacity-90 hover:opacity-100">
            <ArrowLeft className="w-4 h-4" /> {t.appName}
          </Link>
          <span
            data-testid="share-live-pill"
            className={`inline-flex items-center gap-1.5 px-2 py-1 text-[9px] font-bold uppercase tracking-[0.2em] rounded-full transition-colors ${
              live ? "bg-emerald-500 text-white" : "bg-white/15 text-white/85"
            }`}
          >
            <Radio className={`w-3 h-3 ${live ? "animate-pulse" : ""}`} />
            {live ? "LIVE" : "Reconnexion…"}
          </span>
        </div>
        <div className="mt-3 text-[10px] tracking-[0.3em] font-bold uppercase opacity-90 text-mali-yellow">{t.liveAlert}</div>
        <h1 className="font-display text-4xl font-black tracking-tight leading-none mt-1">{label}</h1>
        {alert.description && <p className="mt-3 text-sm leading-relaxed opacity-95">{alert.description}</p>}
      </div>

      <div className="border-b-2 border-rescue-line bg-white px-5 py-4 font-mono text-xs space-y-1.5">
        <div className="flex justify-between"><span className="text-rescue-mute">LATITUDE</span><span className="font-bold">{c.latitude.toFixed(6)}</span></div>
        <div className="flex justify-between"><span className="text-rescue-mute">LONGITUDE</span><span className="font-bold">{c.longitude.toFixed(6)}</span></div>
        {c.accuracy && (<div className="flex justify-between"><span className="text-rescue-mute">PRÉCISION</span><span className="font-bold">±{Math.round(c.accuracy)} m</span></div>)}
      </div>

      <div className="px-5 py-4 border-b-2 border-rescue-line bg-white flex items-center gap-2 text-xs text-rescue-mute">
        <Clock className="w-4 h-4" /> {new Date(alert.created_at).toLocaleString("fr-FR")}
      </div>

      <div className="p-5 space-y-3">
        <a
          href={alert.map_link}
          target="_blank"
          rel="noreferrer"
          data-testid="share-open-map"
          className="w-full bg-rescue-ink text-white py-4 text-center text-xs font-bold uppercase tracking-[0.25em] hover:bg-rescue-red transition-colors flex items-center justify-center gap-2"
        >
          <MapPin className="w-4 h-4" /> {t.openPreciseLocation}
        </a>
        <button
          onClick={copyLink}
          data-testid="share-copy-link"
          className="w-full border-2 border-rescue-ink text-rescue-ink py-4 text-xs font-bold uppercase tracking-[0.25em] hover:bg-rescue-ink hover:text-white transition-colors flex items-center justify-center gap-2"
        >
          <Copy className="w-4 h-4" /> {t.copyShareLink}
        </button>
      </div>

      <InterventionCard alert={alert} />

      <div className="mx-5 mb-8 border-2 border-rescue-line bg-white">
        <div className="px-4 py-3 border-b-2 border-rescue-line text-[10px] font-bold uppercase tracking-[0.25em] text-rescue-mute">
          {t.deliveryReport}
        </div>
        {alert.deliveries.map((d, i) => (
          <div key={`${d.channel}-${d.target}-${i}`} className="px-4 py-3 border-b border-rescue-line last:border-b-0 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[11px] font-bold uppercase tracking-[0.15em]">
                {d.channel === "in_app" ? t.inAppLog : (d.channel === "sms" ? t.channelSms : t.channelEmail)}{d.name ? ` · ${d.name}` : ""}
              </div>
              <div className="font-mono text-[11px] text-rescue-mute truncate">{d.target}</div>
            </div>
            <span className={`text-[10px] font-bold uppercase tracking-[0.2em] ${
              d.status === "sent" ? "text-emerald-700"
                : d.status === "skipped" ? "text-amber-700"
                : "text-rescue-red"
            }`}>{t.delivery[d.status] || d.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
